<template>
  <div
    v-if="campaignLoading || !authStore.authUser || !publisherCampaign"
    class="py-16 flex justify-center text-base-content/25"
  >
    <v-icon
      class="ov-spin"
      name="ri-loader-5-line"
      height="1em"
      width="1em"
      scale="1.5"
    />
  </div>
  <div v-else class="text-sm" style="max-width: 62rem; margin: auto">
    <!-- <QuarantineCampaignAlert 
      v-if="me && publisherCampaign" 
      :publisher="me" 
      :payoutType="publisherCampaign.payoutType"
      className="-mt-6 mb-8" 
    /> -->
    <div class="md:pr-64 relative">
      <div class="absolute hidden md:flex right-0 pl-8 w-64">
        <div class="flex flex-col gap-2 w-full">
          <!-- Campaign Status -->
          <CampaignStatus 
            v-if="status != 'apply to run'"
            :status="status"
            :fullWidth="true"
            size="lg"
            class="text-center"
          />
          
          <!-- Additional Status Info -->
          <template v-if="status == 'subscribed'">
            <div class="flex items-center text-xs">
              <div class="w-1 h-4 bg-primary rounded-full mr-2"></div>
              <span>You are currently <span class="text-primary font-bold">subscribed</span> to this campaign</span>
            </div>
          </template>
          <template v-else-if="status == 'available' && !campaignSubscribeLimitReached">
            <div class="flex items-center text-xs">
              <div class="w-1 h-4 bg-primary rounded-full mr-2"></div>
              <span>This campaign is <span class="text-primary font-bold">available</span>. Agree to the terms below to <span>subscribe</span>.</span>
            </div>
          </template>
          <template v-else-if="status == 'pending approval'">
            <div class="flex items-center text-xs">
              <div class="w-1 h-4 bg-sky-400 rounded-full mr-2"></div>
              <span>This campaign is <span class="text-sky-400 font-bold">pending approval</span> from your Account Manager</span>
            </div>
          </template>
          <template v-else-if="status == 'declined'">
            <div class="flex items-center text-xs">
              <div class="w-1 h-4 bg-error rounded-full mr-2"></div>
              <span>The approval request for this campaign was <span class="text-error font-bold">declined</span> by your Account Manager</span>
            </div>
          </template>
          <template v-else-if="status == 'available' && campaignSubscribeLimitReached">
            <div class="flex items-center text-xs">
              <div class="w-1 h-4 bg-primary rounded-full mr-2"></div>
              <span>You have reached your campaign subscribe limit this month</span>
            </div>
          </template>
          <template v-if="status == 'apply to run'">
            <div
              :class="['flex flex-col gap-2']"
              v-tooltip="
                !termsAccepted
                  ? `You must accept terms before requesting access to this campaign `
                  : campaignSubscribeLimitReached
                  ? `You have reached your campaign subscribe limit this month.`
                  : undefined
              "
            >
              <Modal>
                <template #default="{ toggle }">
                  <XButton
                    class="text-xs !font-semibold border-none opacity-90 py-3 rounded-sm uppercase"
                    color="primary"
                    @click="toggle"
                    :disabled="!termsAccepted || campaignSubscribeLimitReached"
                  >
                    <span>Request Approval</span>
                  </XButton>
                </template>
                <template #modal="{ close }">
                  <RequestCampaignApprovalPanel
                    class="w-full"
                    :campaignId="publisherCampaign.id"
                    @close="close"
                  />
                </template>
              </Modal>
              <div class="flex items-center text-xs mb-2">
              <div class="w-1 h-4 bg-primary rounded-full mr-2"></div>
              <span>This campaign is <span class="text-primary font-bold">available</span> but requires manual approval.</span>
            </div>

            </div>
          </template>
          <div
            v-if="
              publisherCampaign.redirect &&
              publisherCampaign.redirect.status == 'subscribed'
            "
            class="mt-4 mb-1 text-sm uppercase font-normal w-full"
          >
          <template v-if="generalCaps">
              <div class="uppercase text-xs font-medium mb-3 flex">
                <v-icon
                    class="text-accent/80 mr-1"
                    name="md-dangerous-outlined"
                    width="1.3em"
                    height="1.3em"
                  />
                <span>Campaign Cap</span>
              </div>
              <div
                v-for="cap in generalCaps"
                :key="cap.id"
              >
                <CapProgressBar :cap="cap" />
              </div>
            </template>
            <template v-else-if="defaultRedirectCap">
              <div>
                <CapProgressBar :cap="defaultRedirectCap" />
              </div>
            </template>
            <template v-else-if="publisherCampaign.capDefaults">
              <div
                v-for="capDefault in publisherCampaign.capDefaults"
                :key="capDefault.id"
                class="mb-2"
              >
                {{ formatCapToString(capDefault) }}
              </div>
            </template>
          </div>

          <div
            class="border-t border-base-content/10 pt-4 mt-2 flex flex-col gap-4"
          >
            <div
              v-if="
                publisherCampaign && publisherCampaign.allowedRestrictedTags
              "
              class="flex flex-col gap-3 text-xs text-base-content/70 font-normal"
            >
              <div class="flex flex-col gap-2 items-start">
                <div class="flex gap-1.5 items-center">
                  <v-icon
                    class="text-sky-500"
                    name="bi-check"
                    width="1.3em"
                    height="1.3em"
                  />
                  Allowed
                </div>

                <div class="flex items-center">
                  <div
                    class="flex flex-wrap items-center gap-y-1 gap-x-1 text-xxs"
                  >
                    <Tag
                      v-for="tag of allowedTags"
                      :key="tag.name"
                      class="block bg-sky-200 !text-sky-500 border-sky-300 dark:bg-transparent dark:!text-accent border dark:border-primary/30"
                      >{{ tag.name }}</Tag>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-2 items-start">
                <div class="flex gap-1.5 items-center">
                  <v-icon
                    class="text-red-400 mx-[0.15rem]"
                    name="io-close"
                    width="0.95em"
                    height="0.95em"
                  />
                  Restricted
                </div>
                <div class="flex items-center">
                  <div
                    class="flex flex-wrap items-center gap-y-2 gap-x-1 text-xxs"
                  >
                    <Tag
                      v-for="tag of restrictedTags"
                      :key="tag.name"
                      class="block bg-red-200  !text-red-400 border-red-400/25 dark:bg-transparent dark:!text-red-400 dark:border-red-400/30  border"
                      >{{ tag.name }}</Tag>
                  </div>
                </div>
              </div>
              <div
                v-if="license || parsedBaselineDeposits.length"
                class="sidebar-right-list flex flex-col gap-4"
              >
                <dl v-if="license">
                  <dd class="text-black">
                    <Flag
                      :country="license.countryCode"
                      v-tooltip="`${license.label}`"
                      class="rounded h-4"
                    />
                    {{ license.label }}
                  </dd>
                </dl>
                <dl
                  v-if="parsedBaselineDeposits.length"
                  class="flex flex-col gap-2"
                >
                  <dd
                    v-for="(baselineDeposit, index) in parsedBaselineDeposits"
                    :key="index"
                  >
                    <div class="flex">
                      <ul class="deposit-list">
                        <li v-if="hasGeoSpecificBaselines">
                          <span>Geo: </span>{{ baselineDeposit.geo }}
                        </li>
                        <li>
                          <span>Baseline: </span>{{ baselineDeposit.baseline }}
                        </li>
                        <li>
                          <span>Min Deposit: </span
                          >{{ baselineDeposit.minimum }}
                        </li>
                      </ul>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!publisherCampaign">
        <div class="p-8 text-center text-base-content/25">
          <template v-if="campaignLoading">
            <v-icon
              class="ov-spin"
              name="ri-loader-5-line"
              height="1em"
              width="1em"
              scale="1.5"
            />
          </template>
          <template v-else>
            <span>Campaign Not Found</span>
          </template>
        </div>
      </div>

      <div v-if="publisherCampaign">
        <Banner
          v-if="canApply"
          teleportTo="#full-top-alerts"
          bannerClass="bg-info flex md:hidden mx-4 sm:mx-6"
        >
          <div
            class="flex items-center gap-3 text-sm"
            v-tooltip="
              campaignSubscribeLimitReached
                ? `You have reached your campaign subscribe limit this month.`
                : undefined
            "
          >
            <span>This campaign requires manual approval.</span>
            <Modal>
              <template #default="{ toggle }">
                <XButton
                  @click="toggle"
                  :disabled="!termsAccepted || campaignSubscribeLimitReached"
                  class="border-white text-white !btn-xs btn-outline hover:bg-white hover:!text-info"
                  >Request Approval</XButton>
              </template>
              <template #modal="{ close }">
                <RequestCampaignApprovalPanel
                  class="w-full"
                  :campaignId="publisherCampaign.id"
                  @close="close"
                />
              </template>
            </Modal>
          </div>
        </Banner>
        <Banner
          v-if="status == 'pending approval'"
          bannerClass="bg-warning !text-zinc-900 flex md:hidden"
          teleportTo="#full-top-alerts"
        >
          <div class="flex items-center">
            <v-icon name="co-clock" scale="1" class="mr-3 opacity-40" />
            <span>This campaign is currently pending approval.</span>
          </div>
        </Banner>
        <div class="flex gap-8 items-start">
          <div class="flex flex-col gap-2 pt-0">
            <div
              class="rounded-md py-0 w-48 h-32 xl:w-56 xl:h-40 flex justify-center items-center relative"
            >
              <img
                v-if="campaignImages?.length > 0"
                :src="
                  campaignImages[0].conversions.filter(
                    image => image.conversion === '300x200'
                  )[0].storageUrl
                "
                class="object-cover w-full h-full rounded-md block border border-base-100 dark:border-transparent"
              />
              <!-- <img
                v-if="logoImage"
                :src="getImageUrl(logoImage, 'md')"
                :alt="`${publisherCampaign.name} logo`"
                class="h-full w-auto object-contain"
              /> -->

              <!-- <img
                src="@/assets/images/feature-card-blob.svg"
                class="object-contain w-full h-full absolute top-0 left-0 -z-10 opacity-15 min-h-[7rem] scale-[1.25]"
              /> -->
            </div>
          </div>

          <div>
            <h4 class="mb-2">
              <span class="pr-2 text-xl">{{ publisherCampaign.name }}</span>
              <sup class="text-base-content/30 text-sm">{{
                publisherCampaign.id
              }}</sup>
            </h4>
            <div class="pb-2 flex gap-4 items-center">
              <!-- <div
                        class="flex items-center"
                        v-for="tag in publisherCampaign.verticalTags"
                        :key="tag.id"
                    >
                        <Tag color="accent">{{ tag.name }}</Tag>
                    </div> -->
              <div
                class="flex items-center gap-2"
                v-if="
                  (campaignTopGeos && campaignTopGeos.length > 0) ||
                  topGeosLoading
                "
              >
                <!-- <template v-if="topGeosLoading">
                  <div
                    v-for="index in 3"
                    class="skeleton w-5 h-4 rounded-sm bg-zinc-500 opacity-20"
                    :key="index"
                  ></div>
                </template>
                <template v-else>
                  <Flag
                    v-for="country in campaignTopGeos"
                    :key="country.code"
                    :country="country.code"
                    v-tooltip="`${country.name} (Top Geo)`"
                    class="rounded h-4"
                    imageClass
                  />
                </template> -->

                <Tag
                  v-if="publisherCampaign.isCapped"
                  color="darkRed"
                  class="mr-1 mb-1"
                  >CAPPED</Tag>
              </div>

              <!-- TODO: ADD CAPPED FEATURE BACK AS BANNER / NOTIF ON TOP -->
            </div>
            <div
              v-html="publisherCampaign.description"
              class="mt-4 mb-3 campaign-description text-sm"
            ></div>

            <div
              v-if="publisherCampaign.terms"
              v-html="publisherCampaign.terms"
              class="mt-4 mb-3 campaign-terms text-sm text-base-content/50"
            ></div>

            <div
              v-if="
                publisherCampaign.creativeUrl &&
                publisherCampaign.redirect &&
                publisherCampaign.redirect.status == 'subscribed'
              "
              class="mt-6"
            >
              <a
                :href="publisherCampaign.creativeUrl"
                target="blank"
                class="text-primary underline hover:opacity-70 font-normal flex gap-1 items-center"
                >Creative Here
                <v-icon
                  name="md-openinnew-round"
                  height="1em"
                  width="1em"
                  scale="0.8"
                />
              </a>
            </div>
          </div>
        </div>

        <div class="mt-8 py-2" v-show="isExpanded == true">
          <div class="mb-4">
            <XTable
              :data="visiblePayouts || []"
              class="w-full"
              :scroll-x="false"
            >
              <template v-slot:header-row>
                <th>Payouts</th>
                <th></th>
                <th v-if="geoCaps || defaultCountryCap">
                  Caps
                </th>
              </template>
              <template v-slot:row="props">
                <td>
                  <div class="flex gap-2">
                    <template v-if="props.row.country?.code == null">
                      All Countries
                    </template>
                    <template v-else>
                      <div class="flex gap-2">
                        <Flag
                          :country="props.row.country.code"
                          v-tooltip="`${props.row.country.name}`"
                          class="rounded !h-5 !w-auto"
                        />
                        {{ props.row.country.code }} -
                        {{ props.row.country.name }}
                      </div>
                    </template>
                    <template v-if="props.row.sourceId">
                      <span class="text-base-content/50">+</span>
                      <span v-tooltip="`Keyword`" class="border border-base-content/25 rounded-md px-1 py-0.5 text-base-content/50 text-xs">KW</span>
                      {{ props.row.keyword ?? 'Missing Keyword (' + props.row.sourceId + ')'}}
                    </template>
                  </div>
                </td>
                <td>
                  <template v-if="props.row.rate">
                    {{ formatPayout(props.row.rate, props.row.type) }}
                  </template>
                  <template v-else-if="props.row.prettyType">
                    {{ props.row.prettyType }}
                  </template>
                </td>
                <td v-if="geoCaps || defaultCountryCap" className="w-40">
                  <div class="flex gap-2 justify-end">
                    <template v-if="props.row.country?.code == null">
                      All Countries
                    </template>
                  <template v-else>
                    <CapProgressBar 
                      v-if="geoCapsByCountryCode && geoCapsByCountryCode[props.row.country.code] || defaultCountryCap" 
                      :use-short-text="true"
                      :cap="geoCapsByCountryCode && geoCapsByCountryCode[props.row.country.code] || defaultCountryCap" 
                      class="w-44 text-xxs"
                    />
                    </template>
                  </div>
                </td>
              </template> 
            </XTable>

            <div class="py-1 mt-1" v-if="filteredPayouts.length > showPayoutsLength">
              <div class="flex justify-between items-center">
                <template v-if="payoutsAreExpanded">
                  <a class="link" @click.prevent="togglePayoutsAreExpanded"
                    >Collapse</a
                  >
                </template>
                <template v-else>
                  <div class="flex items-center">
                    <span class="text-base-content/50 mr-4"
                      >+ {{ invisiblePayoutsLength }} more geos</span
                    >
                    <a
                      class="link block"
                      @click.prevent="togglePayoutsAreExpanded"
                      >View All</a
                    >
                  </div>
                </template>
                <div>
                  <a class="link" @click="exportPayouts">Export Payouts</a>
                </div>
              </div>
            </div>

            <!-- Keyword Blocks Note -->
            <div v-if="hasKeywordBlocks" class="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-md">
              <div class="flex items-center gap-2 text-sm">
                <v-icon name="bi-exclamation-triangle" class="text-warning" scale="1" />
                <span class="text-base-content/80">
                  You have <strong>{{ keywordBlocks.length }}</strong> blocked source{{ keywordBlocks.length > 1 ? 's' : '' }} on your account.
                </span>
                <Modal>
                  <template #default="{ toggle }">
                    <a class="link text-warning" @click="toggle">View Details</a>
                  </template>
                  <template #modal="{ close }">
                    <KeywordBlocksModal
                      :keywordBlocks="me?.keywordBlocks || []"
                      @close="close"
                    />
                  </template>
                </Modal>
              </div>
            </div>
          </div>

          <!-- Source Caps Table -->
          <div class="mb-4" v-if="sourceCaps && sourceCaps.length > 0">
            <XTable
              :data="visibleSourceCaps || []"
              class="w-full"
              :scroll-x="false"
            >
              <template v-slot:header-row>
                <th>Keyword</th>
                <th>Geo</th>
                <th>Caps</th>
              </template>
              <template v-slot:row="props">
                <td>
                  {{ getSourceKeywordFromTriggerables(props.row.triggerables) }}
                </td>
                <td>
                  <div class="flex gap-2">
                    <template v-if="getIdFromTriggerableKey(props.row.triggerableKey, 'country')">
                      <Flag
                        :country="getIdFromTriggerableKey(props.row.triggerableKey, 'country')"
                        v-tooltip="`${getIdFromTriggerableKey(props.row.triggerableKey, 'country')}`"
                        class="rounded !h-5 !w-auto"
                      />
                      {{ getIdFromTriggerableKey(props.row.triggerableKey, 'country')?.toUpperCase() }}
                    </template>
                    <template v-else>
                      All Countries
                    </template>
                  </div>
                </td>
                <td className="w-40">
                  <div class="flex gap-2 justify-end">
                    <CapProgressBar 
                      :use-short-text="true"
                      :cap="props.row" 
                      class="w-44 text-xxs"
                    />
                  </div>
                </td>
              </template> 
            </XTable>

            <div class="py-1 mt-1" v-if="sourceCaps.length > showSourceCapsLength">
              <div class="flex justify-between items-center">
                <template v-if="sourcesCapsAreExpanded">
                  <a class="link" @click.prevent="toggleSourceCapsAreExpanded"
                    >Collapse</a
                  >
                </template>
                <template v-else>
                  <div class="flex items-center">
                    <span class="text-base-content/50 mr-4"
                      >+ {{ invisibleSourceCapsLength }} more sources</span
                    >
                    <a
                      class="link block"
                      @click.prevent="toggleSourceCapsAreExpanded"
                      >View All</a
                    >
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="flex sm:hidden flex-row mb-4">
            <div class="panel flex flex-col w-2/3 !border-r-0 !rounded-r-none">
              <div class="panel-header">
                <span class="text-xs font-regular uppercase">Allowed</span>
              </div>
              <div
                class="panel-body !border-t-0 !bg-base-200  flex-grow flex items-center !rounded-r-none"
              >
                <div class="flex flex-wrap items-center gap-y-1 gap-x-1 text-xxs">
                  <Tag
                    v-for="tag of allowedTags"
                    :key="tag.name"
                    class="block bg-sky-200 !text-sky-500 border-sky-300 dark:bg-transparent dark:!text-accent border dark:border-primary/30"
                  >
                    {{ tag.name }}
                  </Tag>
                </div>
              </div>
            </div>

            <div class="panel flex flex-col w-1/3 !border-l-0 !rounded-l-none">
              <div class="panel-header">
                <span class="text-xs font-regular">RESTRICTED</span>
              </div>
              <div
                class="panel-body !border-t-0 !bg-base-200 flex-grow flex items-center !rounded-l-none"
              >
                <div class="flex flex-wrap items-center gap-y-1 gap-x-1 text-xxs">
                  <Tag
                    v-for="tag of restrictedTags"
                    :key="tag.name"
                    class="block bg-red-200 !text-red-400 border-red-400/25 dark:bg-transparent dark:!text-red-400 dark:border-red-400/30 border"
                  >
                    {{ tag.name }}
                  </Tag>
                </div>
              </div>
            </div>
          </div>

          <div v-if="termsAccepted && !canApply" class="panel mb-4 relative">
            <div
              v-if="
                publisherCampaign?.redirect?.status != 'subscribed' &&
                campaignSubscribeLimitReached
              "
              class="text-center absolute h-full w-full rounded-md z-10 overflow-hidden"
            >
              <div
                class="flex items-center justify-center bg-base-200/90 h-full w-full overflow-hidden rounded-md"
              >
                <v-icon
                  v-tooltip="
                    `Your campaign subscription limit has been reached for this month.`
                  "
                  class="text-base-content dark:text-white w-10 h-10"
                  name="bi-shield-lock"
                />
              </div>
            </div>

            <div class="panel-header">
              <div>
                <div>
                  <span class="text-xs">TRACKING LINKS & PARAMETERS</span>
                </div>
              </div>
            </div>
            <div  
                v-if="
                        publisherCampaign.redirect &&
                        publisherCampaign.redirect.status == 'subscribed'
                      "
              class="panel-header">
              <RedirectParameters
                v-if="me"
                :user="me"
                layout="inline"
                @value="updateRedirectParameters"
                :disable-saving="true"
              />
            </div>
            <div
              class="panel-body !border-t-0"
              v-if="
                publisherCampaign.redirect &&
                publisherCampaign.redirect.status == 'unsubscribed'
              "
            >
              <div>
                <p>
                  Your account has been
                  <span class="font-bold text-error">unsubscribed</span>
                  from this campaign. Please contact your account manager for
                  more information.
                </p>
              </div>
            </div>
            <div v-else class="panel-header">
              <div
                v-if="
                  publisherCampaign.offers &&
                  publisherCampaign.offers.length > 0
                "
              >
                <div
                  class="flex items-center mb-2"
                  v-for="offer in publisherCampaign.offers"
                  :key="offer.id"
                >
                  <div class="mr-4 w-20 h-20">
                    <img
                      :src="offer.legacyImageThumbnailUrl"
                      :width="20"
                      :height="20"
                      class="rounded w-20 h-20 block object-contain cursor-pointer hover:ring-2 ring-base-content/25"
                      :data-source="offer.legacyImageUrl"
                      v-viewer="{}"
                    />
                  </div>

                  <div>
                    <p class="mb-1 fw-500">
                      <template
                        v-if="
                          offer.destinationType == 'direct' ||
                          (offer.template && offer.template.path == 'redirect')
                        "
                      >
                        {{ offer.landingPageName ?? "Direct" }}
                      </template>
                      <template v-else-if="offer.template">
                        {{
                          offer.template.name
                            ? offer.template.name
                            : "Missing Name"
                        }}
                      </template>
                    </p>
                    <div
                      v-if="
                        publisherCampaign.redirect &&
                        publisherCampaign.redirect.status == 'subscribed'
                      "
                      class="mt-2"
                    >
                      <div class="bg-base-100 rounded-md p-3 border border-base-300">
                        <div class="flex items-center justify-between gap-3">
                          <code class="text-sm text-base-content/80 break-all font-mono flex-1 min-w-0">
                            {{ publisherCampaign.redirect.url }}/o/{{ offer.encodedId }}{{ redirectParametersString }}
                          </code>
                          <CopyToClipboard
                            class="flex-shrink-0"
                            size="sm"
                            :value="`${publisherCampaign.redirect.url}/o/${offer.encodedId}${redirectParametersString}`"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else-if="
                  publisherCampaign.redirect &&
                  publisherCampaign.redirect.status == 'subscribed'
                "
                class="mb-2"
              >
                <div>
                  <div class="font-semibold text-base-content mb-2 text-sm">Your Link</div>
                  <div class="bg-base-100 rounded-md p-3 border border-base-300">
                    <div class="flex items-center justify-between gap-3">
                      <code class="text-sm text-base-content/80 break-all text-xs font-mono flex-1 min-w-0">
                        {{ publisherCampaign.redirect.url }}{{ redirectParametersString }}
                      </code>
                      <CopyToClipboard
                        class="flex-shrink-0"
                        size="sm"
                        :value="`${publisherCampaign.redirect.url}${redirectParametersString}`"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="
                  status == 'available' &&
                  (!publisherCampaign.redirect ||
                    (publisherCampaign.redirect &&
                      !publisherCampaign.redirect.status == 'approved'))
                "
                class="mt-6"
              >
                <p class="mb-3">
                  <span
                    >Agree to the terms below to generate your tracking
                    links:</span
                  >
                </p>
                <Banner variant="info" :simple="true" bannerClass="rounded-lg">
                  <div class="flex items-center justify-between w-full gap-4">
                    <Checkbox
                      checkboxClass="checkbox checkbox-info"
                      v-model="agreedToAllowed"
                    >
                      <template v-slot:label>
                        I acknowledge and agree that I will only send the
                        "Allowed" traffic listed above.
                      </template>
                    </Checkbox>
                    <XButton
                      class="btn-info shadow-none"
                      :disabled="
                        !agreedToAllowed || campaignSubscribeLimitReached
                      "
                      @click="agreeToAllowedTraffic"
                      :is-loading="isAgreeing"
                      >I Agree</XButton>
                  </div>
                </Banner>
              </div>
            </div>
          </div>

          <div v-if="publisherCampaign.redirect" class="my-4">
            <XTable
              :data="publisherCampaign.redirect.postbacks"
              class="overflow-x-scroll my-3"
            >
              <template v-slot:header-row>
                <th class="!max-w-64">YOUR POSTBACKS</th>
                <!-- <th>Conversion Type</th> -->
                <th>
                  <Modal>
                    <template #default="{ toggle }">
                      <div class="flex justify-end -my-3 -mx-2 rounded-sm">
                        <XButton
                          v-tooltip="`Add Campaign Postback`"
                          v-show="!hasDefaultPostback"
                          class="btn hover:bg-base-content/5 border-none shadow-none link-primary"
                          @click="toggle()"
                          :disabled="hasDefaultPostback"
                          ><v-icon name="hi-plus-sm"
                        /></XButton>
                      </div>
                    </template>
                    <template #modal="{ close }">
                      <CreatePostbackPanel
                        class="w-full"
                        :campaignId="id"
                        @close="close"
                      />
                    </template>
                  </Modal>
                </th>
              </template>
              <template v-slot:empty>
                <Modal>
                  <template #default="{ toggle }">
                    <div class="flex justify-end -my-3 -mx-2 rounded-sm">
                      <XButton
                        v-tooltip="`Add Campaign Postback`"
                        v-show="!hasDefaultPostback"
                        class="bg-transparent hover:bg-base-content/5 border-none shadow-none link-primary"
                        @click="toggle()"
                        :disabled="hasDefaultPostback"
                        ><span class="flex items-center gap-2"
                          ><v-icon name="hi-plus-sm" /> Add Campaign
                          Postback</span
                        ></XButton>
                    </div>
                  </template>
                  <template #modal="{ close }">
                    <CreatePostbackPanel
                      class="w-full"
                      :campaignId="id"
                      @close="close"
                    />
                  </template>
                </Modal>
              </template>
              <template v-slot:row="props">
                <td>
                  <div class="max-w-xs line-clamp-1">
                    {{ props.row.fullUrl }}
                  </div>
                </td>
                <!-- <td>
                  <div class="cursor-pointer">
                    <div class="flex align-center text-xs">
                      <Tag color="accent" v-if="props.row.conversionType">{{
                        props.row.groupLabel ??
                        props.row.conversionType?.name ??
                        "Default"
                      }}</Tag>
                      <Tag v-else color="accent">Default</Tag>

                      <v-icon
                        name="bi-info-circle"
                        class="text-info ml-2"
                        v-tooltip="{
                          content: getConversionTypeDescription(
                            props.row.groupLabel ??
                              props.row.conversionType?.name ??
                              'Default',
                          ),
                          html: true,
                        }"
                      />
                    </div>
                  </div>
                </td> -->
                <td>
                  <div class="flex justify-end">
                    <Modal>
                      <template #default="{ toggle }">
                        <a
                          v-tooltip="`Edit`"
                          href="#"
                          class="link text-base-content/50 hover:text-base-content p-1"
                          @click.prevent="toggle"
                        >
                          <v-icon name="la-edit-solid" />
                        </a>
                      </template>
                      <template #modal="{ close }">
                        <UpdatePostbackPanel
                          class="w-full"
                          :id="props.row.id"
                          @close="close"
                        />
                      </template>
                    </Modal>

                    <Modal>
                      <template #default="{ toggle }">
                        <a
                          v-tooltip="`Delete`"
                          href="#"
                          class="text-base-content/50 hover:text-red-600 p-1"
                          @click.prevent="toggle"
                        >
                          <v-icon name="io-close" />
                        </a>
                      </template>
                      <template #modal="{ close }">
                        <DeletePostbackPanel
                          class="w-full"
                          :postback="props.row"
                          :campaignId="id"
                          @close="close"
                        />
                      </template>
                    </Modal>
                  </div></td
              ></template>
            </XTable>
            <div class="py-0 flex justify-between items-center">
              <div class="text-base-content/50 text-xs flex items-center">
                <v-icon
                  class="mr-2"
                  name="co-warning"
                  height="1em"
                  width="1em"
                ></v-icon>
                <span>
                  These campaign specific postbacks will override any
                  <router-link
                    :to="{ name: 'Postbacks' }"
                    class="link hover:text-base-content/70 underline font-semibold"
                    >account level postbacks</router-link
                  >.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { useQuery, useMutation } from "@vue/apollo-composable"
import { useAuthStore } from "@/stores/auth.js"
import { useRoute } from "vue-router"
import PUBLISHER_CAMPAIGN_QUERY from "@/graphql/PublisherCampaign/PublisherCampaignQuery.gql"
import AGREE_TO_ALLOWED_TRAFFIC_MUTATION from "@/graphql/PublisherCampaign/AgreeToAllowedTrafficMutation.gql"
import ME_QUERY from "@/graphql/User/MeQuery.gql"
import CAMPAIGN_TOP_GEOS_QUERY from "@/graphql/Report/CampaignTopGeosQuery.gql"
import TAGS_QUERY from "@/graphql/Tag/TagsQuery.gql"
import QuarantineCampaignAlert from "@/pages/campaigns/QuarantineCampaignAlert.vue"
import Banner from "@/components/common/Banner.vue"
import ProgressBar from "@/components/common/ProgressBar.vue"
import XButton from "@/components/common/XButton.vue"
import Modal from "@/components/common/Modal.vue"
import Checkbox from "@/components/common/Checkbox.vue"
import CopyToClipboard from "@/components/common/CopyToClipboard.vue"
import CampaignStatus from "@/components/common/CampaignStatus.vue"
import RequestCampaignApprovalPanel from "@/components/common/RequestCampaignApprovalPanel.vue"
import KeywordBlocksModal from "@/components/common/KeywordBlocksModal.vue"
import RedirectParameters from "@/components/common/RedirectParameters.vue"
import CreatePostbackPanel from "@/pages/postbacks/CreatePostbackPanel.vue"
import UpdatePostbackPanel from "@/pages/postbacks/UpdatePostbackPanel.vue"
import DeletePostbackPanel from "@/pages/postbacks/DeletePostbackPanel.vue"
import { useConversionTypes } from "@/composables/useConversionTypes.js"
import { useCampaignImages } from "@/composables/useCampaignImages.js"
import CapProgressBar from '@/components/common/CapProgressBar.vue'

const { campaignImages, logoImage, getImageUrl } = useCampaignImages()
const { getConversionTypeDescription } = useConversionTypes()

// import PostbackDelete from '@/views/Publisher/Postbacks/PostbackDelete'
// import PostbackEdit from '@/views/Publisher/Postbacks/PostbackEdit'
// import ConversionTypeDescription from '@/mixins/ConversionTypeDescriptionMixing.js'
import { saveAs } from "file-saver"
import { formatPayout } from "@/utils/formatCurrency"

// Apollo and Pinia
const authStore = useAuthStore()

const route = useRoute()

const id = route.params.id

const showCampaignApprovalModal = ref(false)

// Reactive data and props
const isExpanded = ref(true)
const payoutsAreExpanded = ref(false)
const redirectParameters = ref(null)
const agreedToAllowed = ref(false)
const isAgreeing = ref(false)
const showPayoutsLength = ref(3)
const redirectParametersString = ref("")
const page = ref(1)
const perPage = ref(1)
const campaignRedirects = ref(false)

// Source caps variables
const showSourceCapsLength = ref(3)
const sourcesCapsAreExpanded = ref(false)

// publisher schema now returns Campaign.license as a structured object
// (`License { countryCode, label }`) — backend cast Campaign.license to
// array, so no client-side JSON.parse needed anymore.
const license = computed(() => publisherCampaign.value?.license ?? null)

const parsedBaselineDeposits = computed(() => {
  if (!publisherCampaign.value?.baselineDeposits) return []
  return JSON.parse(publisherCampaign.value.baselineDeposits)
})

const hasGeoSpecificBaselines = computed(() =>
  parsedBaselineDeposits.value.some((d) => d.geo !== 'Default'),
)

// TODO: We are only checking for the default cap defaults. We need to check first if there's a specific one for the redirect and country, and just redirect / null country, or null redirect and specific country

const defaultCountryCap = computed(() => {
  if (publisherCampaign && publisherCampaign.value) {

    if(!publisherCampaign.value.capDefaults){
      return undefined
    }

    // Find the first capDefault that meets the criteria for a default country cap.
    const defaultCap = publisherCampaign.value.capDefaults.find(capDefault => {
      // Check if the triggerablePairs array exists and is an array.
      if (!capDefault.triggerablePairs || !Array.isArray(capDefault.triggerablePairs)) {
        return false; // Skip if triggerablePairs is missing or not an array.
      }

      // Check if the array contains a pair with type 'Country' and id null.
      const hasDefaultCountry = capDefault.triggerablePairs.some(pair => pair.type === 'Country' && pair.id === null);
      // Check if the array contains a pair with type 'Redirect' and id null.
      const hasDefaultRedirect = capDefault.triggerablePairs.some(pair => pair.type === 'Redirect' && pair.id === null);

      // Return true if both conditions are met.
      return hasDefaultCountry && hasDefaultRedirect;
    });

    // Return the found default cap, or undefined if none was found.
    return defaultCap;
  }
  // Return undefined if publisherCampaign is not available.
  return undefined;
});

const defaultRedirectCap = computed(() => {
  if (publisherCampaign && publisherCampaign.value) {
    // Find the first capDefault that meets the criteria for a default country cap.
    const defaultCap = publisherCampaign.value.capDefaults.find(capDefault => {
      // Check if the triggerablePairs array exists and is an array.
      if (!capDefault.triggerablePairs || !Array.isArray(capDefault.triggerablePairs)) {
        return false; // Skip if triggerablePairs is missing or not an array.
      }

      // Check if the array contains a pair with type 'Country' and id null.
      const hasDefaultCountry = capDefault.triggerablePairs.some(pair => pair.type === 'Country' && pair.id === null);
      // Check if the array contains a pair with type 'Redirect' and id null.
      const hasDefaultRedirect = capDefault.triggerablePairs.some(pair => pair.type === 'Redirect' && pair.id === null);

      // Return true if both conditions are met.
      return !hasDefaultCountry && hasDefaultRedirect;
    });

    // Return the found default cap, or undefined if none was found.
    return defaultCap;
  }
  // Return undefined if publisherCampaign is not available.
  return undefined;
});

const status = computed(() =>
  publisherCampaign && publisherCampaign.value
    ? publisherCampaign.value.status
    : "",
)

const generalCaps = computed(() => {
  const redirectId = publisherCampaign.value?.redirect?.id
  if (!redirectId) return null;
  const caps = publisherCampaign.value?.caps ?? []
  if (caps.length == 0) return null;
  const generalCaps = caps.filter(cap =>  cap.triggerableKey === "redirect_" + redirectId)
  if (generalCaps.length == 0) return null;
  return generalCaps
})


const geoCaps = computed(() => {
  const redirectId = publisherCampaign.value?.redirect?.id
  if (!redirectId) return null;
  const caps = publisherCampaign.value?.caps ?? []
  if (caps.length == 0) return null;

  // need to check that triggerableKey contains the words "country_" and "redirect_"
  const output = caps.filter(cap =>  cap.triggerableKey.includes("country_") && cap.triggerableKey.includes("redirect_"))
  
  if(output.length == 0) return null;

  return output
})  

const sourceCaps = computed(() => {
  const redirectId = publisherCampaign.value?.redirect?.id
  if (!redirectId) return null;
  const caps = publisherCampaign.value?.caps
  if (!caps) return null;

  // Filter caps that contain source_ in the triggerableKey
  const output = caps.filter(cap => cap.triggerableKey.includes("source_"))
  
  if(output.length == 0) return null;

  return output
})  


const visibleSourceCaps = computed(() => {
  if (!sourceCaps.value) return []
  return sourcesCapsAreExpanded.value
    ? sourceCaps.value
    : sourceCaps.value.slice(0, showSourceCapsLength.value)
})

const invisibleSourceCapsLength = computed(() => {
  if (!sourceCaps.value) return 0
  return sourceCaps.value.length - visibleSourceCaps.value.length
})

function toggleSourceCapsAreExpanded() {
  sourcesCapsAreExpanded.value = !sourcesCapsAreExpanded.value
}



const geoCapsByCountryCode = computed(() => {
  if (!geoCaps.value) return null;
  // need to return an object with geo code keys and caps as values
  const geoCapsByCountryCode = {}
  geoCaps.value.forEach(cap => {
    const countryCode = cap.triggerableKey.split("_")[1]
    geoCapsByCountryCode[countryCode.toUpperCase()] = cap
  })

  return geoCapsByCountryCode
})

const canApply = computed(() => {
  return status.value == "apply to run" && termsAccepted.value
})

const getSourceKeywordFromTriggerables = (triggerables) => {
  const source = triggerables.find(triggerable => triggerable.triggerable?.__typename === 'Source')
  return source?.triggerable?.keyword && source.triggerable?.keyword != '' ?  source.triggerable?.keyword : 'Missing Keyword (' + source?.triggerable?.id + ')'
}

const showPostbackCreateModal = ref(false)

// Apollo queries
const { result: meResult, loading: meLoading } = useQuery(ME_QUERY)

const me = computed(() => {
  return meResult.value?.me
})

const keywordBlocks = computed(() => {
  return me.value?.keywordBlocks?.filter(block => block.keyword) || []
})

const hasKeywordBlocks = computed(() => keywordBlocks.value.length > 0)

const {
  result: campaignResult,
  loading: campaignLoading,
  refetch: refetchCampaign,
  onResult,
} = useQuery(
  PUBLISHER_CAMPAIGN_QUERY,
  {
    publisherId: authStore.authUser?.id,
    id: id,
  },
  {
    enabled: authStore.authUser,
  },
)

onResult(({ data, errors }) => {
  if (data && data.publisherCampaign) {
    campaignImages.value = data.publisherCampaign.images
  }
})

const publisherCampaign = computed(
  () => campaignResult.value?.publisherCampaign,
)

const { result: topGeosResult, loading: topGeosLoading } = useQuery(
  CAMPAIGN_TOP_GEOS_QUERY,
  {
    campaignId: id,
  },
)
const campaignTopGeos = computed(() => topGeosResult.value?.campaignTopGeos)

// Traffic type tags query (same approach as ytrack-frontend)
const { result: trafficTypeTagsResult } = useQuery(TAGS_QUERY, {
  first: 999,
  page: 1,
  filters: {
    type: {
      value: ['trafficType']
    }
  }
})

const trafficTypeTags = computed(() => trafficTypeTagsResult.value?.tags)

// Apollo mutation
const { mutate: agreeToAllowedTrafficMutation, loading: agreeLoading } =
  useMutation(AGREE_TO_ALLOWED_TRAFFIC_MUTATION, {
    update(cache, { data }) {
      // Evict the "me" query from cache to force refetch
      cache.evict({ fieldName: "me" })
      cache.gc()
    }
  })

const termsAccepted = computed(() => {
  return me.value?.userDetails?.agreedToTerms
})

const payoutType = computed(() => {
  return publisherCampaign.value?.payoutType
})

const subscriptionLimits = computed(() => {
      return me.value?.publisherRestrictions.filter(restriction => restriction.type === 'campaign-subscription')
   
   console.log('subscriptionLimits', subscriptionLimits)
    });

const subscriptionLimit = computed(() => {
  if (!subscriptionLimits.value) return null;
  return subscriptionLimits.value.find(
    limit => limit.data.campaignType.toLowerCase() === payoutType.value.toLowerCase()
  )?.data;
});


    
const currentSubscriptionLimit = computed(() => {
  if (!subscriptionLimit.value) return null;

  return subscriptionLimit?.value || 0;
})

const campaignSubscribeLimitReached = computed(() => {
  const subscriptionLimit = currentSubscriptionLimit.value


  if (subscriptionLimit == null) {
    return false
  }

  
  return subscriptionLimit.subscribeCount >= subscriptionLimit.maxSubscriptions
})

// Tags computed properties (same approach as ytrack-frontend)
const restrictedTags = computed(() => {
  if (!publisherCampaign.value) {
    return []
  }
  if (!publisherCampaign.value.allowedRestrictedTags) {
    return []
  }
  const filtered = publisherCampaign.value.allowedRestrictedTags.filter(tag => tag.isNot)
  console.log('restrictedTags: filtered result:', filtered)
  console.log('restrictedTags: publisherCampaign.value.allowedRestrictedTags:', publisherCampaign.value.allowedRestrictedTags)
  return filtered
})

const restrictedTagNames = computed(() => {
  const names = restrictedTags.value.map(tag => tag.name)
  return names
})

const allowedTags = computed(() => {
  if (!publisherCampaign.value || !trafficTypeTags.value?.data) {
    return []
  }
  
  const tags = trafficTypeTags.value.data
  
  const filtered = tags.filter(tag => !restrictedTagNames.value.includes(tag.name))
  return filtered
})

const hasDefaultPostback = computed(() => {
  if (!publisherCampaign.value) return false
  return publisherCampaign.value?.redirect?.hasDefaultPostback === true
})

const filteredPayouts = computed(() => {
  if (!publisherCampaign.value) return []

  let allPayouts = []
  if (
    publisherCampaign.value.status !== "subscribed" &&
    publisherCampaign.value.payoutsGlobal?.length > 0
  ) {
    allPayouts = [...publisherCampaign.value.payoutsGlobal]
  } else {
    allPayouts = [...publisherCampaign.value.payouts]
  }

  return allPayouts;
})

const getIdFromTriggerableKey = (triggerableKey, type) => {
  // break the triggerableKey into an object with the type as key and id as value
  const result = triggerableKey.split('__').reduce((acc, item) => {
  const [type, id] = item.split('_');
    acc[type] = id;
    return acc;
  }, {});

  return result[type]
}


const visiblePayouts = computed(() => {
  if (!publisherCampaign.value) return []
  const allPayouts = [...filteredPayouts.value]
  return payoutsAreExpanded.value
    ? allPayouts
    : allPayouts.slice(0, showPayoutsLength.value)
})

const invisiblePayoutsLength = computed(() => {
  return filteredPayouts.value.length - visiblePayouts.value.length
})

// Methods
function agreeToAllowedTraffic() {
  agreeToAllowedTrafficMutation({ campaignId: publisherCampaign.value.id })
    .then(() => {
      // Handle success, e.g., refetch queries
      refetchCampaign()
    })
    .catch(error => {
      console.error("Error in agreeToAllowedTraffic:", error)
    })
}

function updateRedirectParameters(params) {
  redirectParameters.value = params
  redirectParametersString.value = Object.entries(params)
    .filter(param => param[1])
    .map(param => `${param[0]}=${param[1]}`)
    .join("&")
  redirectParametersString.value = redirectParametersString.value
    ? "?" + redirectParametersString.value
    : ""
}

function formatCapToString(cap) {
  return cap.type === "payout"
    ? `$ ${cap.triggerValue} ${cap.frequency} Cap`
    : `${cap.triggerValue} ${cap.frequency} Actions Cap`
}

function togglePayoutsAreExpanded() {
  payoutsAreExpanded.value = !payoutsAreExpanded.value
}

function exportPayouts() {
  let payouts =
    publisherCampaign.value.status !== "subscribed" &&
    publisherCampaign.value.payoutsGlobal.length > 0
      ? publisherCampaign.value.payoutsGlobal
      : publisherCampaign.value.payouts

  const mappedPayouts = payouts.map(payout => ({
    geo: payout.country?.code ?? "ALL",
    country: payout.country?.name ?? "All Countries",
    source: payout.keyword,
    payout: payout.rate,
  }))

  const headers = ["Geo", "Country", "Source", "Payout"]
  const rows = mappedPayouts.map(item => Object.values(item).join(","))
  const csvContent = [headers.join(","), ...rows].join("\n")
  const blob = new Blob([csvContent], { type: "text/csv" })
  saveAs(blob, `campaign_${id}_payouts.csv`)
}

function openRequestApprovalModal() {
  showCampaignApprovalModal.value = true
}

function openCreatePostbackModal() {
  showPostbackCreateModal.value = true
}

function hideCampaignApprovalModal() {
  showCampaignApprovalModal.value = false
}

// Lifecycle hooks
onMounted(() => {
  // Any additional setup
})

onBeforeUnmount(() => {
  // Cleanup listeners
})
</script>

<style scoped>
.sidebar-right-list {
  @apply mt-6 border rounded-lg border-neutral p-4 flex flex-col gap-4;
}

.sidebar-right-list dl {
  @apply rounded-lg flex flex-col gap-2;
}

.sidebar-right-list dt {
  @apply flex items-center gap-2;
}

.sidebar-right-list dd {
  @apply text-xs capitalize dark:font-thin dark:text-white/80  flex items-center gap-2;
}

.sidebar-right-list dd ul {
  @apply text-base-content dark:text-white/80;
}

ul.deposit-list li span {
  @apply font-light text-xs;
}

.campaign-description a {
  @apply link link-info;
}
</style>
